import { useEffect, useRef, useState } from 'react'
import { Paperclip, X } from 'lucide-react'
import type { Item, Prioriteit, TicketCategorie, TicketSoort } from '../../../gedeeld/types'
import { api, upload } from '../../lib/api'
import { bytes } from '../../lib/opmaak'
import { navigeer } from '../../lib/router'
import { gewijzigd, meld, meldFout, useMensen, useStaat, useVestigingen } from '../../lib/staat'
import { CATEGORIE, itemSoort, PRIORITEIT, ticketNummer, TICKET_SOORT } from '../../lib/woorden'
import { Groeiveld, KiesVestiging } from '../stukjes'
import { Kies, KiesMens, Knop, Veld, Venster } from '../ui'

/* ===========================================================================
 *  Een probleem melden
 *
 *  Zo kort als het kan: wat is er aan de hand, en een paar keuzes die IT
 *  helpen sorteren. Een foto van de foutmelding kan erbij -- vaak sneller
 *  dan hem overtypen.
 *
 *  Spoed kan een medewerker niet zelf kiezen: dat zet de database terug op
 *  hoog (0165). Dat staat er hier eerlijk bij, in plaats van dat het stil
 *  verandert.
 *
 *  IT kan namens iemand anders melden (wie belde) en het meteen toewijzen.
 * =========================================================================== */

export default function NieuwTicket({ itemId, itemNaam, systeemId, categorie, onSluit }: {
  itemId?: string | null
  /** Van een gescande sticker: het apparaat hoeft niet in je eigen lijst te staan. */
  itemNaam?: string | null
  systeemId?: string | null
  categorie?: string
  onSluit: () => void
}) {
  const ik = useStaat((s) => s.ik)!
  const beheerder = useStaat((s) => s.beheerder)
  const vestigingen = useVestigingen()
  const mensen = useMensen()
  const [titel, setTitel] = useState('')
  const [omschrijving, setOmschrijving] = useState('')
  const [soort, setSoort] = useState<TicketSoort>('storing')
  const [cat, setCat] = useState<TicketCategorie>((categorie as TicketCategorie) ?? 'overig')
  const [prio, setPrio] = useState<Prioriteit>('normaal')
  const [vestiging, setVestiging] = useState<string | null>(ik.vestiging)
  const [item, setItem] = useState<string | null>(itemId ?? null)
  const [melder, setMelder] = useState<string | null>(null)
  const [toegewezen, setToegewezen] = useState<string | null>(null)
  const [bestanden, setBestanden] = useState<File[]>([])
  const [items, setItems] = useState<Item[]>([])
  const [bezig, setBezig] = useState(false)
  const kiezer = useRef<HTMLInputElement>(null)

  /* Wat jij hebt (of, voor IT, alles), om het ticket aan te hangen. */
  useEffect(() => { void api.items().then(setItems).catch(() => {}) }, [])

  function voegToe(lijst: FileList | File[] | null) {
    if (!lijst) return
    const nieuw = [...lijst].filter((f) => {
      if (f.size > 15 * 1024 * 1024) { meld(`${f.name} is groter dan 15 MB.`, 'fout'); return false }
      return true
    })
    setBestanden((b) => [...b, ...nieuw].slice(0, 10))
  }

  /* Een schermafdruk plakken (Ctrl+V) werkt ook. */
  useEffect(() => {
    const plak = (e: ClipboardEvent) => {
      const f = [...(e.clipboardData?.files ?? [])]
      if (f.length) { e.preventDefault(); voegToe(f) }
    }
    window.addEventListener('paste', plak)
    return () => window.removeEventListener('paste', plak)
  }, [])

  async function verstuur() {
    if (!titel.trim()) { meld('Zet er in een paar woorden bij wat er aan de hand is.', 'fout'); return }
    setBezig(true)
    try {
      const t = await api.ticketMelden({
        titel, omschrijving, soort, categorie: cat, prioriteit: prio,
        vestigingId: vestiging, itemId: item, systeemId: systeemId ?? null,
        ...(beheerder ? { melderId: melder ?? undefined, toegewezenAan: toegewezen } : {}),
      })
      let mislukt = 0
      for (const f of bestanden) {
        try { await upload('ticket', t.id, f) } catch { mislukt++ }
      }
      gewijzigd()
      meld(mislukt
        ? `${ticketNummer(t.nummer)} is gemeld, maar ${mislukt} bijlage(n) kwamen niet mee. Voeg ze in het ticket opnieuw toe.`
        : `${ticketNummer(t.nummer)} is gemeld. IT krijgt er een seintje van.`, mislukt ? 'fout' : 'ok')
      onSluit()
      navigeer(`/t/${t.id}`)
    } catch (e) {
      meldFout(e)
    } finally {
      setBezig(false)
    }
  }

  const prioOpties = PRIORITEIT.filter((p) => beheerder || p.key !== 'spoed')

  return (
    <Venster titel="Een probleem melden" onSluit={onSluit} breed={640} bewustSluiten
      voet={<>
        <span className="vul veld-hulp">Ctrl+Enter verstuurt.</span>
        <Knop soort="stil" onClick={onSluit}>Annuleren</Knop>
        <Knop soort="hoofd" bezig={bezig} onClick={() => void verstuur()}>Melden</Knop>
      </>}>
      <Veld label="Wat is er aan de hand?">
        <input className="invoer groot" autoFocus value={titel} maxLength={200} onChange={(e) => setTitel(e.target.value)}
          placeholder="Bijvoorbeeld: printer bij de kassa print niet"
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void verstuur() }} />
      </Veld>
      <Veld label="Vertel meer (optioneel)" hulp="Wat zie je, sinds wanneer, wat heb je al geprobeerd? Een foto of schermafdruk mag erbij; plakken kan ook.">
        <Groeiveld waarde={omschrijving} onWaarde={setOmschrijving} min={4} onCtrlEnter={() => void verstuur()} />
      </Veld>

      <div className="bijlagen-nieuw">
        {bestanden.map((f, n) => (
          <span key={n} className="bijlage-pil">
            <Paperclip size={12} /> <span className="afkap">{f.name}</span> <span className="stil">{bytes(f.size)}</span>
            <button type="button" className="mini" aria-label="Weghalen" onClick={() => setBestanden(bestanden.filter((_, x) => x !== n))}><X size={12} /></button>
          </span>
        ))}
        <Knop klein soort="gewoon" icoon={<Paperclip size={14} />} onClick={() => kiezer.current?.click()}>Foto of bestand</Knop>
        <input ref={kiezer} type="file" multiple hidden accept="image/*,application/pdf,.txt,.log,.docx,.xlsx,.csv,.eml,.msg,.zip"
          onChange={(e) => { voegToe(e.target.files); e.target.value = '' }} />
      </div>

      <div className="formulier">
        <Veld label="Soort">
          <Kies<TicketSoort> waarde={soort} opties={TICKET_SOORT} onKies={setSoort} />
        </Veld>
        <Veld label="Waarover">
          <Kies<TicketCategorie> waarde={cat} opties={CATEGORIE} onKies={setCat} />
        </Veld>
        <Veld label="Hoe dringend" hulp={beheerder ? undefined : 'Ligt de vestiging stil? Bel dan ook even.'}>
          <Kies<Prioriteit> waarde={prio} onKies={setPrio}
            opties={prioOpties.map((p) => ({ key: p.key, label: `${p.label} — ${p.uitleg}`, kleur: p.kleur }))} />
        </Veld>
        <Veld label="Vestiging">
          <KiesVestiging waarde={vestiging} vestigingen={vestigingen} onKies={setVestiging} leeg="Geen / kantoor" />
        </Veld>
        {(items.length > 0 || itemNaam) && (
          <Veld label="Over welk apparaat (optioneel)" breed>
            <Kies<string> waarde={item ?? ''} leeg="Geen apparaat"
              opties={[{ key: '', label: 'Geen apparaat' },
                ...(itemId && itemNaam && !items.some((i) => i.id === itemId) ? [{ key: itemId, label: `${itemNaam} (van de sticker)` }] : []),
                ...items.map((i) => ({
                key: i.id, label: `${i.naam} · ${itemSoort(i.soort).label}${i.houderNaam && beheerder ? ` · ${i.houderNaam}` : ''}`,
              }))]}
              onKies={(x) => setItem(x || null)} />
          </Veld>
        )}
        {beheerder && (
          <>
            <Veld label="Gemeld door" hulp="Leeg = jijzelf. Kies iemand als die belde of mailde.">
              <KiesMens waarde={melder} mensen={mensen} onKies={(m) => setMelder(m?.id ?? null)} leegTekst="Ikzelf" />
            </Veld>
            <Veld label="Toewijzen aan">
              <KiesMens waarde={toegewezen} mensen={mensen.filter((m) => m.beheert)}
                onKies={(m) => setToegewezen(m?.id ?? null)} leegTekst="Nog niemand" />
            </Veld>
          </>
        )}
      </div>
    </Venster>
  )
}
